import type { ReactNode } from 'react';
import { Box, Tab, Tabs } from '@mui/material';
import { NavLink, useLocation } from 'react-router';

export interface SectionTab {
  to: string;
  label: string;
  badge?: number;
  /** Matches only the exact path (for index tabs like "/assessments"). */
  end?: boolean;
  icon?: ReactNode;
}

/** Secondary navigation inside a section (module sub-pages, people, admin). */
export function SectionTabs({ items, label }: { items: SectionTab[]; label: string }) {
  const { pathname } = useLocation();
  const current =
    [...items]
      .sort((a, b) => b.to.length - a.to.length)
      .find((item) => (item.end ? pathname === item.to : pathname.startsWith(item.to)))?.to ??
    false;

  return (
    <Tabs
      value={current}
      variant="scrollable"
      scrollButtons={false}
      aria-label={label}
      sx={{
        mb: 3,
        borderBottom: 1,
        borderColor: 'divider',
        mx: { xs: -2, sm: 0 },
        px: { xs: 1, sm: 0 },
      }}
    >
      {items.map((item) => (
        <Tab
          key={item.to}
          value={item.to}
          component={NavLink}
          to={item.to}
          end={item.end}
          iconPosition="start"
          icon={item.icon as React.ReactElement | undefined}
          label={
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
              {item.label}
              {Boolean(item.badge) && (
                <Box
                  component="span"
                  aria-label={`${item.badge} pendentes`}
                  sx={(theme) => ({
                    minWidth: 22,
                    height: 20,
                    px: 0.75,
                    borderRadius: 99,
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    lineHeight: '20px',
                    textAlign: 'center',
                    bgcolor: theme.vars.palette.secondary.main,
                    color: theme.vars.palette.secondary.contrastText,
                  })}
                >
                  {item.badge! > 99 ? '99+' : item.badge}
                </Box>
              )}
            </Box>
          }
          sx={{ minHeight: 48 }}
        />
      ))}
    </Tabs>
  );
}
